<script setup lang="ts">
import * as d3 from "d3";
import { nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from "vue";

const COLOR_COUNT = 8;

const {
  traces,
  xRange,
  traceNames = undefined,
  margin = { top: 20, right: 10, bottom: 10, left: 10 },
  xAxisTickFormat = ".0f",
  yAxisTickFormat = ".0f",
  xAxisLegend = "",
  yAxisLegend = "",
} = defineProps<{
  traces: number[][];
  xRange: [number, number];
  traceNames?: string[];
  margin?: { top: number; right: number; bottom: number; left: number };
  xAxisTickFormat?: string;
  yAxisTickFormat?: string;
  xAxisLegend?: string;
  yAxisLegend?: string;
}>();

const plot = useTemplateRef<SVGElement>("plot");

function updatePlot() {
  if (plot.value) {
    const width = plot.value.clientWidth;
    const height = plot.value.clientHeight;
    const svg = d3.select(plot.value);
    const axesG = svg.select<SVGGElement>(".axes");
    const axisY = axesG.select<SVGGElement>(".y");
    const axisX = axesG.select<SVGGElement>(".x");
    const curvesG = svg.select<SVGGElement>(".curves");

    // Y axis (values)
    const [yMin, yMax] = d3.extent(traces.flat()) as [number, number];
    const yScale = d3
      .scaleLinear()
      .range([margin.top, height - margin.bottom])
      .domain([yMax, yMin]);

    axisY.attr("transform", `translate(${margin.left}, 0)`);

    // compute dynamic left margin from longest Y tick label
    const yTickTexts = axisY.selectAll<SVGTextElement, unknown>(".tick text");
    let maxLabelWidth = 0;
    yTickTexts.each(function () {
      const bbox = (this as SVGTextElement).getBBox();
      if (bbox.width > maxLabelWidth) {
        maxLabelWidth = bbox.width;
      }
    });
    const leftMargin = Math.max(margin.left, maxLabelWidth + 10);

    // X axis (range)
    const maxTraceLength = d3.max(traces, (trace) => trace.length) ?? 1;
    const xScale = d3
      .scaleLinear()
      .range([leftMargin, width - margin.right - leftMargin])
      .domain([0, maxTraceLength - 1]);

    // Create a formatter that maps indices to xRange values
    const xAxisFormatter = (domainValue: d3.NumberValue) => {
      const tickValue = typeof domainValue === "number" ? domainValue : domainValue.valueOf();
      const t = tickValue / (maxTraceLength - 1); // Normalize to [0, 1]
      const value = xRange[0] + t * (xRange[1] - xRange[0]);
      return d3.format(xAxisTickFormat)(value);
    };

    // render Y axis with correct tickSize and position using computed leftMargin
    axisY
      .attr("transform", `translate(${leftMargin}, 0)`)
      .transition()
      .duration(200)
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-xScale(maxTraceLength - 1) + leftMargin)
          .tickFormat(d3.format(yAxisTickFormat))
      );

    axisX
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .transition()
      .duration(200)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-yScale(yMin) + margin.top)
          .tickFormat(xAxisFormatter)
      );

    // also move Y axis to match new left margin
    axisY.attr("transform", `translate(${leftMargin}, 0)`);

    // Create line generator
    const line = d3
      .line<number>()
      .x((_, i) => xScale(i))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    // Bind data to path elements for each trace
    const curvesSelection = curvesG
      .selectAll<SVGPathElement, number[]>(".curve")
      .data(traces, (_, i) => `trace-${i}`);

    const enter = curvesSelection.enter().append("path").attr("class", "curve");

    const merged = enter.merge(curvesSelection);
    merged
      .transition()
      .duration(200)
      .attr("d", (d) => line(d) ?? "")
      .attr("class", (_, i) => `curve trace-${i % COLOR_COUNT}`)
      .attr("fill", "none")
      .attr("stroke-width", 2);
  }
}

const resizeObserver = new ResizeObserver(() => {
  updatePlot();
});

watch(
  () => [traces],
  () => {
    updatePlot();
  }
);

onMounted(async () => {
  // be sure that container has its full size already
  await nextTick();
  if (plot.value) {
    updatePlot();
    resizeObserver.observe(plot.value);
  }
});

onBeforeUnmount(() => {
  if (plot.value) {
    resizeObserver.unobserve(plot.value);
  }
});
</script>

<template>
  <div class="curve-plot">
    <div class="plot-container">
      <svg ref="plot">
        <g class="curves"></g>
        <g class="axes">
          <g class="x"></g>
          <g class="y"></g>
        </g>
      </svg>
      <div v-if="yAxisLegend.length > 0" class="y-legend text-xs-regular">
        {{ yAxisLegend }}
      </div>
    </div>
    <div v-if="xAxisLegend.length > 0" class="x-legend text-xs-regular">
      {{ xAxisLegend }}
    </div>
    <div v-if="traceNames && traceNames.length > 0" class="traces-legend">
      <div
        v-for="(trace, index) in traceNames"
        :key="index"
        class="trace-legend text-xs-regular"
        :class="[`trace-${index % COLOR_COUNT}`]"
      >
        <div>
          {{ trace }}
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.curve-plot {
  --plotly-green: #00cc96;
  --plotly-purple: #ab63fa;
  --plotly-light-orange: #ffa15a;
  --plotly-cyan: #19d3f3;
  --plotly-pink: #ff6692;
  --plotly-light-green: #b6e880;

  display: flex;
  height: 100%;
  flex-direction: column;

  .plot-container {
    display: flex;
    flex: 1;
    flex-direction: row-reverse;
  }

  svg {
    flex: 1;
  }

  text {
    font-family: GeistMono, monospace;
  }

  g.axes {
    path.domain {
      stroke: transparent;
      stroke-width: 0;
    }

    .tick line {
      opacity: 0.2;
      stroke: var(--content-secondary);
      stroke-dasharray: 2;
      stroke-width: var(--stroke-width-md);
    }

    .tick text {
      fill: var(--content-secondary);
    }
  }

  .curve {
    fill: none;
    stroke-width: 2;
  }

  /* Color-blind friendly palette inspired by Plotly */
  .trace-0 {
    stroke: var(--color-brand-blue-300); /* similar to Plotly #636EFA */
  }

  .trace-1 {
    stroke: var(--color-brand-orange-300); /* similar to Plotly #EF553B */
  }

  .trace-2 {
    stroke: var(--plotly-green);
  }

  .trace-3 {
    stroke: var(--plotly-purple);
  }

  .trace-4 {
    stroke: var(--plotly-light-orange);
  }

  .trace-5 {
    stroke: var(--plotly-cyan);
  }

  .trace-6 {
    stroke: var(--plotly-pink);
  }

  .trace-7 {
    stroke: var(--plotly-light-green);
  }

  .x-legend {
    padding-top: var(--numbers-8);
    color: var(--content-secondary);
    text-align: center;
  }

  .y-legend {
    color: var(--content-secondary);
    text-align: center;
    writing-mode: sideways-lr;
  }

  .traces-legend {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    padding: var(--numbers-8);
    color: var(--content-tertiary);
    gap: var(--numbers-8);

    > div {
      position: relative;
      padding-left: var(--numbers-20);

      &::before {
        position: absolute;
        top: 0;
        left: 0;
        width: var(--numbers-16);
        height: var(--numbers-16);
        border-radius: var(--numbers-16);
        content: " ";
      }

      &.trace-0::before {
        background-color: var(--color-brand-blue-300);
      }

      &.trace-1::before {
        background-color: var(--color-brand-orange-300);
      }

      &.trace-2::before {
        background-color: var(--plotly-green);
      }

      &.trace-3::before {
        background-color: var(--plotly-purple);
      }

      &.trace-4::before {
        background-color: var(--plotly-light-orange);
      }

      &.trace-5::before {
        background-color: var(--plotly-cyan);
      }

      &.trace-6::before {
        background-color: var(--plotly-pink);
      }

      &.trace-7::before {
        background-color: var(--plotly-light-green);
      }
    }
  }
}
</style>
